import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const fadeUp = {
 initial: { opacity: 0, y: 30 },
 animate: { opacity: 1, y: 0 },
 transition: { duration: 0.6 },
};

// Person writing with question marks - thinking/questioning
const illustration = "/images/founder-story.png";

export default function FounderStoryPage() {
 return (
  <div className="flex flex-col">
   {/* Hero */}
   <section className="bg-hero py-16 sm:py-20 lg:py-24">
    <div className="container-narrow">
     <motion.div
      initial="initial"
      animate="animate"
      variants={fadeUp}
      className="text-center"
     >
      <h1 className="heading-display text-foreground mb-6">
       The Founder Story
      </h1>
      <p className="body-large text-muted-foreground max-w-2xl mx-auto">
       Why I started Let's Build Startup, and what it means to build something real.
      </p>
     </motion.div>
    </div>
   </section>

   {/* Story Content */}
   <section className="bg-background py-16 sm:py-20 lg:py-24">
    <div className="container-narrow">
     <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
     >
      {/* Illustration */}
      <div className="flex justify-center mb-12">
       <div className="w-full max-w-xs">
        <img
         src={illustration}
         alt="Founder thinking and questioning"
         className="w-full h-auto"
        />
       </div>
      </div>

      <div className="space-y-8">
       <p className="text-xl text-muted-foreground leading-relaxed">
        I did not set out to build Let's Build Startup. Like most things in life, it came from necessity, failure, and a hard-earned education in what actually matters.
       </p>

       <Separator />

       <div>
        <h2 className="font-serif text-2xl font-medium text-foreground mb-5">The Early Days</h2>
        
        <p className="text-muted-foreground leading-relaxed mb-4">
         My first venture was typical founder hubris. I was convinced I had a great idea. I was convinced people would pay for it. I was convinced that if I just built it well enough, they would come.
        </p>
        
        <p className="text-muted-foreground leading-relaxed">
         I spent months building. I hired people. I burned through savings. And when I finally launched, I learned what every founder eventually learns: the market does not care about your convictions.
        </p>
       </div>

       <div className="bg-muted/30 border-l-4 border-accent p-6 my-10 rounded-r-xl">
        <Quote className="h-6 w-6 text-accent mb-3" />
        <p className="text-lg text-foreground italic leading-relaxed">
         The product I spent a year building took 6 months to admit was wrong, and another 6 to shut down. By then, I had burned through relationships, health, and most of my runway.
        </p>
       </div>

       <div>
        <h2 className="font-serif text-2xl font-medium text-foreground mb-5">The Lesson</h2>
        
        <p className="text-muted-foreground leading-relaxed mb-4">
         The failure was not the product. It was not the team. It was not the execution. It was the timing of clarity.
        </p>
        
        <p className="text-muted-foreground leading-relaxed mb-4">
         I learned too late that the idea needed work. I learned too late that my assumptions were wrong. I learned too late that people would not pay what I thought they would.
        </p>
        
        <p className="text-foreground font-medium">
         If I had spent 3 weeks validating instead of 3 months building, everything would have been different.
        </p>
       </div>

       <Separator />

       <div>
        <h2 className="font-serif text-2xl font-medium text-foreground mb-5">What Came Next</h2>
        
        <p className="text-muted-foreground leading-relaxed mb-4">
         After that failure, I did what a lot of founders do: I took on consulting work to pay the bills. I helped other founders with their products, their launches, their problems.
        </p>
        
        <p className="text-muted-foreground leading-relaxed mb-4">
         And I started to see the same patterns everywhere. Founders building before validating. Founders scaling confusion. Founders spending months (and fortunes) on products that the market did not want.
        </p>
        
        <p className="text-muted-foreground leading-relaxed">
         The ones who succeeded? They were the ones who got clarity early. Who tested assumptions. Who were willing to hear no before they invested everything.
        </p>
       </div>

       <div>
        <h2 className="font-serif text-2xl font-medium text-foreground mb-5">Why Let's Build Startup Exists</h2>
        
        <p className="text-muted-foreground leading-relaxed mb-4">
         Let's Build Startup exists because I wanted to give other founders what I did not have: a partner who understands the stakes, who can help cut through uncertainty, and who cares about outcomes. not just deliverables.
        </p>
        
        <p className="text-muted-foreground leading-relaxed">
         We are not an agency. We are not consultants. We are founders helping founders. with the hard-won lessons that only come from lived experience.
        </p>
       </div>

       <div className="bg-card border border-border/50 rounded-xl p-8 my-10">
        <h3 className="font-serif text-xl font-medium text-foreground mb-5">What I Believe</h3>
        <ul className="space-y-3">
         {[
          "Early validation is the best investment a founder can make",
          "Clarity before commitment saves more than money. it saves time, relationships, and sanity",
          "Honest feedback is more valuable than comfortable validation",
          "Building something real beats building something big",
          "The best partnerships are founder-to-founder, not client-to-vendor",
         ].map((belief, i) => (
          <motion.li 
           key={i} 
           className="flex items-start gap-3 text-muted-foreground"
           initial={{ opacity: 0, x: -10 }}
           whileInView={{ opacity: 1, x: 0 }}
           viewport={{ once: true }}
           transition={{ delay: i * 0.1 }}
          >
           <span className="text-accent mt-1">•</span>
           {belief}
          </motion.li>
         ))}
        </ul>
       </div>

       <p className="text-foreground text-lg leading-relaxed">
        If any of this resonates, I would be happy to talk. Not to sell you anything. just to understand where you are and see if there is a way we can help.
       </p>
      </div>
     </motion.article>
    </div>
   </section>

   {/* CTA */}
   <section className="bg-primary text-primary-foreground py-16 sm:py-20">
    <div className="container-narrow text-center">
     <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
     >
      <h2 className="heading-section mb-5">Want to Connect?</h2>
      <p className="body-base text-primary-foreground/80 mb-6">
       Share what you are working on. Let us see if there is a fit.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
       <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full">
        <Link to="/contact">
         Submit Your Wishlist
         <ArrowRight className="h-4 w-4 ml-2" />
        </Link>
       </Button>
       <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full">
        <Link to="/about">About the Company</Link>
       </Button>
      </div>
     </motion.div>
    </div>
   </section>
  </div>
 );
}
